import React, {
  useState,
  ReactElement,
  PropsWithChildren,
  MouseEventHandler,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  type Avatar,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  ToolbarGroup,
  Masthead,
  MastheadContent,
  MastheadMain,
  MastheadLogo,
  MastheadToggle,
  MastheadBrand,
  Dropdown,
  DropdownList,
  type DropdownItemProps,
  DropdownItem,
  Button,
  type Brand,
  PageToggleButton,
  ButtonVariant,
  BrandProps,
  AvatarProps,
  Page,
  SkipToContentProps,
  type SkipToContent,
  PageSidebarProps,
  type PageSidebar,
  NotificationDrawer,
  NotificationDrawerHeader,
  type NotificationDrawerListItem,
  NotificationDrawerBody,
  EmptyState,
  EmptyStateVariant,
  NotificationDrawerList,
  NotificationDrawerListItemProps,
  NotificationBadge,
  MenuToggleElement,
  MenuToggle,
} from "@patternfly/react-core";
import CogIcon from "@patternfly/react-icons/dist/esm/icons/cog-icon";
import BarsIcon from "@patternfly/react-icons/dist/esm/icons/bars-icon";
import BellIcon from "@patternfly/react-icons/dist/esm/icons/bell-icon";

export interface ApplicationLayoutProps {
  mainContainerId?: string;
  skipToContent?: ReactElement<SkipToContentProps, typeof SkipToContent>;
  sidebar: ReactElement<PageSidebarProps, typeof PageSidebar>;
  userDropdownItems: ReactElement<DropdownItemProps, typeof DropdownItem>[];
  brand: ReactElement<BrandProps, typeof Brand>;
  hideHeaderToolbar?: boolean;
  name?: string;
  avatar?: ReactElement<AvatarProps, typeof Avatar>;
  isNotificationDrawerExpanded?: boolean;
  notifications?:
    | ReactElement<
        NotificationDrawerListItemProps,
        typeof NotificationDrawerListItem
      >[]
    | ReactElement<
        NotificationDrawerListItemProps,
        typeof NotificationDrawerListItem
      >;
  onCogClick?: MouseEventHandler<HTMLButtonElement>;
  onNotificationDrawerClose?: () => void;
  onNotificationBellClick?: () => void;
}

export default ApplicationLayout;

function ApplicationLayout({
  userDropdownItems,
  brand,
  name,
  avatar,
  children,
  mainContainerId,
  skipToContent,
  sidebar,
  onCogClick,
  isNotificationDrawerExpanded,
  onNotificationBellClick,
  onNotificationDrawerClose,
  notifications = [],
  hideHeaderToolbar = false,
}: PropsWithChildren<ApplicationLayoutProps>) {
  const drawerRef = useRef<HTMLElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const unreadNotificationCount = useMemo(() => {
    if (notifications instanceof Array) {
      return notifications.filter((item) => !item.props.isRead).length;
    } else if (!notifications.props.isRead) {
      return 1;
    } else {
      return 0;
    }
  }, [notifications]);

  const handleDrawerExpand = useCallback(() => {
    if (drawerRef.current !== null) {
      const firstTabbableItem = drawerRef.current.querySelector(
        "a, button",
      ) as HTMLElement;
      firstTabbableItem.focus();
    }
  }, []);

  const notificationDrawer = (
    <NotificationDrawer ref={drawerRef}>
      <NotificationDrawerHeader
        count={unreadNotificationCount}
        onClose={onNotificationDrawerClose}
      ></NotificationDrawerHeader>
      <NotificationDrawerBody>
        {unreadNotificationCount > 0 && (
          <NotificationDrawerList>{notifications}</NotificationDrawerList>
        )}
        {unreadNotificationCount === 0 && (
          <EmptyState
            headingLevel="h2"
            icon={BellIcon}
            titleText="No notifications"
            variant={EmptyStateVariant.full}
          ></EmptyState>
        )}
      </NotificationDrawerBody>
    </NotificationDrawer>
  );

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((isOpen) => !isOpen);
  }, []);

  const onDropdownSelect = useCallback(() => {
    setIsDropdownOpen((isOpen) => !isOpen);
  }, []);

  const dropdownToggle = useCallback(
    (toggleRef: React.Ref<MenuToggleElement>) => (
      <MenuToggle
        data-testid="user-dropdown-toggle"
        ref={toggleRef}
        icon={avatar}
        onClick={onDropdownToggle}
        isExpanded={isDropdownOpen}
      >
        {name}
      </MenuToggle>
    ),
    [isDropdownOpen, onDropdownToggle, name, avatar],
  );

  let headerToolbar: React.JSX.Element | null = null;
  if (!hideHeaderToolbar) {
    headerToolbar = (
      <Toolbar id="toolbar" isFullHeight isStatic>
        <ToolbarContent>
          <ToolbarGroup
            variant="action-group-plain"
            align={{ default: "alignEnd" }}
            gap={{ default: "gapNone", md: "gapMd" }}
          >
            <ToolbarItem>
              <NotificationBadge
                onClick={onNotificationBellClick}
                aria-label="Notifications"
                isExpanded={isNotificationDrawerExpanded}
                variant={unreadNotificationCount > 0 ? "unread" : "read"}
                icon={<BellIcon />}
              ></NotificationBadge>
            </ToolbarItem>
            <ToolbarGroup
              variant="action-group-plain"
              visibility={{ default: "hidden", lg: "visible" }}
            >
              <ToolbarItem>
                <Button
                  onClick={onCogClick}
                  aria-label="Settings"
                  variant={ButtonVariant.plain}
                  icon={<CogIcon />}
                />
              </ToolbarItem>
            </ToolbarGroup>
          </ToolbarGroup>
          <ToolbarItem visibility={{ default: "hidden", md: "visible" }}>
            <Dropdown
              onSelect={onDropdownSelect}
              isOpen={isDropdownOpen}
              toggle={dropdownToggle}
            >
              <DropdownList>{userDropdownItems}</DropdownList>
            </Dropdown>
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
    );
  }

  const masthead = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <PageToggleButton
            variant="plain"
            aria-label="Global navigation"
            icon={<BarsIcon />}
          ></PageToggleButton>
        </MastheadToggle>
        <MastheadBrand data-codemods>
          <MastheadLogo data-codemods component="a">
            {brand}
          </MastheadLogo>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>{headerToolbar}</MastheadContent>
    </Masthead>
  );

  return (
    <Page
      masthead={masthead}
      sidebar={sidebar}
      isManagedSidebar
      isNotificationDrawerExpanded={isNotificationDrawerExpanded}
      onNotificationDrawerExpand={handleDrawerExpand}
      notificationDrawer={notificationDrawer}
      skipToContent={skipToContent}
      mainContainerId={mainContainerId}
      isHorizontalSubnavWidthLimited
      isHorizontalSubnavGrouped
    >
      {children}
    </Page>
  );
}
